import { useAuth0 } from '@auth0/auth0-react';
import { useBackoffice } from '../../backoffice/useBackoffice';
import { keepPreviousData, useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';

export type KycDoc = {
  id: string;
  kyc_verification_id: string;
  doc_type: 'id_document';
  doc_subtype: 'other';
  doc_side?: 'front';
  status: 'received_from_consumer';
  version: number;
  bucket_file_key: string;
  created_at: string;
  updated_at: string;
  description: string;
};

export const useKycDocuments = (customerId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const { developerId, isBackofficeOn } = useBackoffice();
  const query = useQuery({
    queryKey: ['kyc-docs', developerId, customerId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const res = await axios.get<{
        data: {
          items: KycDoc[];
        };
      }>(`${process.env.REACT_APP_BACKEND_API_URL}/customers/${customerId}/kyc-docs`, {
        params: {
          consumerId: developerId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res.data?.data;
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5,
    enabled: isBackofficeOn && !!customerId,
  });

  return query;
};

export const useUpdateKycDocument = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { developerId } = useBackoffice();
  const query = useMutation({
    mutationKey: ['kyc-doc'],
    mutationFn: async ({
      customerId,
      documentId,
      payload,
    }: {
      customerId: string;
      documentId: string;
      payload: Partial<KycDoc>;
    }) => {
      const token = await getAccessTokenSilently();
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/customers/${customerId}/kyc-docs/${documentId}`,
        payload,
        {
          params: {
            consumerId: developerId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return res.data?.data;
    },
  });

  return query;
};
